import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from '../../lib/utils'

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
        success:
          "border-transparent bg-[hsl(var(--primary-dark)_/_0.1)] text-[hsl(var(--primary-dark))]",
        warning:
          "border-transparent bg-chart-warning/15 text-chart-warning",
        info:
          "border-transparent bg-primary/10 text-foreground",
        /** Neutral pill for attribute/tag display — no emphasis */
        pill:
          "border-transparent bg-muted hover:bg-muted/80 text-foreground font-normal cursor-default",
        /** Tiny metadata tag — used for audience type/tier, status pills */
        tag:
          "border-0 bg-muted text-muted-foreground rounded-full text-xs font-normal px-2 py-0",
        /** Neutral soft badge — metadata, attributes, inactive states */
        muted:
          "border-transparent bg-muted text-muted-foreground font-normal",
        /** Brand-accent tinted badge — active/highlighted entities */
        accent:
          "border border-brand-accent/30 bg-brand-accent/15 text-brand-accent font-medium",
        /** Positive/success soft badge — healthy status, enabled states */
        positive:
          "border-transparent bg-chart-positive/[0.12] text-chart-positive font-medium",
        /** Soft-destructive badge — error/warning without full destructive weight */
        "soft-destructive":
          "border-transparent bg-destructive/15 text-destructive font-medium",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
  VariantProps<typeof badgeVariants> { }

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => {
    return (
      <div ref={ref} className={cn(badgeVariants({ variant }), className)} {...props} />
    )
  }
)
Badge.displayName = 'Badge'

export { Badge, badgeVariants }
